import { ChangeDetectionStrategy, Component, Injector, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AuthService } from "@auth0/auth0-angular";
import { first, tap } from "rxjs/operators";
import { firstValueFrom } from "rxjs";

import { CollaborationTokenService } from "./services/collaboration-token.service";
import { CollaborationDeepLinkState, CollaborationTokenStoreService } from "./services/collaboration-token-store.service";
import { CollaborationLoggerService } from "./services/collaboration-logger.service";
import { ContentPipe } from "../services/content/content.pipe";
import { CollaborationNavigationService } from "./services/collaboration-navigation.service";
import { DialogsManagerService } from "../services/dialogs-manager.service";
import { ConfirmationDialogComponent } from "../shared/confirmation-dialog/confirmation-dialog.component";
import { ConfirmationDialog } from "../shared/confirmation-dialog/confirmation-dialog.interface";

@Component({
    standalone: false,
    selector: 'collaboration-auth-proxy',
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollaborationAuthProxyComponent implements OnInit {
    private readonly logOptions = { component: 'CollaborationAuthProxyComponent' };

    constructor(
        private route: ActivatedRoute,
        private contentPipe: ContentPipe,
        private tokenService: CollaborationTokenService,
        private tokenStore: CollaborationTokenStoreService,
        private logger: CollaborationLoggerService,
        private auth: AuthService,
        private injector: Injector,
        private dialogManager: DialogsManagerService,
    ) {}

    private get navigationService(): CollaborationNavigationService {
        return this.injector.get(CollaborationNavigationService);
    }

    ngOnInit(): void {
        this.init().catch(error => {
            this.navigateToAuthenticationErrorPage(
                this.contentPipe.transform('ai-assessments-collaboration.generalAuthFailureMessage', {
                    params: {
                        error: (error as Error)?.message || String(error)
                    }
                })
            );
        });
    }

    private async init(): Promise<void> {
        this.logger.info('Collaboration auth proxy initialization started', {}, this.logOptions);

        try {
            const params = this.route.snapshot.queryParams;

            // Auth0 callback: code + state (manual handling when skipRedirectCallback is true)
            if (params['code'] && params['state']) {
                return this.handleAuth0Callback();
            }

            // Auth0 callback with authenticated & assessmentPageId (legacy/redirect case)
            const isCallback = !!params['authenticated'] && !!params['assessmentPageId'];

            if (isCallback) {
                const assessmentId = params['assessmentPageId'];

                if (!assessmentId) {
                    this.logger.warn('Auth0 callback without assessmentId', {}, this.logOptions);
                    this.navigateToAuthenticationErrorPage(
                        this.contentPipe.transform('ai-assessments-collaboration.generalAuthFailureMessage', {
                            params: { error: 'No assessmentId in callback' }
                        })
                    );
                    return;
                }

                try {
                    const isValid = await this.tokenService.verifyTokenAndSession();

                    if (!isValid) {
                        this.logger.warn('Failed to verify Auth0 session after callback', { assessmentId }, this.logOptions);
                        this.navigateToAuthenticationErrorPage(
                            this.contentPipe.transform('ai-assessments-collaboration.generalAuthFailureMessage', {
                                params: { error: 'Failed to verify session after Auth0 callback' }
                            })
                        );
                        return;
                    }

                    this.logger.info('Auth0 session verified, navigating to assessment page', { assessmentId }, this.logOptions);
                    this.tokenStore.setActiveAssessmentId(assessmentId);
                    const deepLinkState = this.extractDeepLinkState(params);
                    if (deepLinkState) this.tokenStore.setDeepLinkState(deepLinkState);
                    this.navigateToAssessmentPage(assessmentId);
                }
                catch (error) {
                    this.logger.logError(error as Error, {
                        error: (error as Error)?.message || String(error),
                        errorStack: (error as Error)?.stack,
                        assessmentId,
                    }, this.logOptions);
                    this.navigateToAuthenticationErrorPage(error);
                }
                return;
            }

            // Initial invitation link
            const newCompanyId = params['companyId'] as string | undefined;
            const collaboratorEmail = (params['email'] as string | undefined)?.replace(/ /g, '+');
            const assessmentId = params['assessmentPageId'] as string | undefined;
            const companyName = params['companyName'] as string | undefined;

            if (!assessmentId) {
                this.logger.warn('No relevant params found in auth proxy', {}, this.logOptions);
                this.navigateToAuthenticationErrorPage(
                    this.contentPipe.transform('ai-assessments-collaboration.generalAuthFailureMessage', {
                        params: { error: 'No relevant params found in auth proxy' }
                    })
                );
                return;
            }

            const deepLinkState = this.extractDeepLinkState(params);

            const currentCompanyId = this.tokenStore.getCompanyId();

            // Switching to a different company requires user confirmation
            if (currentCompanyId && newCompanyId && currentCompanyId !== newCompanyId) {
                this.logger.info('Company switch detected, showing confirmation dialog', {
                    currentCompanyId,
                    newCompanyId,
                }, this.logOptions);
                this.showSwitchCompanyDialog(newCompanyId, collaboratorEmail, assessmentId, companyName, deepLinkState);
                return;
            }

            this.storeSessionAndNavigate(newCompanyId, collaboratorEmail, assessmentId, companyName, deepLinkState);
        }
        catch (error) {
            this.logger.logError(error as Error, {
                error: (error as Error)?.message || String(error),
                errorStack: (error as Error)?.stack,
            }, this.logOptions);
            this.navigateToAuthenticationErrorPage(error);
        }
    }

    private showSwitchCompanyDialog(newCompanyId: string, collaboratorEmail: string, assessmentId: string, companyName?: string, deepLinkState?: CollaborationDeepLinkState): void {
        const dialogData: ConfirmationDialog = {
            title: this.contentPipe.transform('ai-assessments-collaboration.switchCompanyDialogTitle'),
            text: this.contentPipe.transform('ai-assessments-collaboration.switchCompanyDialogText'),
            okBtn: this.contentPipe.transform('common.okBtn'),
            cancelBtn: this.contentPipe.transform('common.cancelBtn'),
        };

        this.dialogManager.openDialogCommon(ConfirmationDialogComponent, dialogData, '500px', true)
            .afterClosed$
            .pipe(
                first(),
                tap(confirmed => {
                    if (confirmed) {
                        this.logger.info('User confirmed company switch', { newCompanyId }, this.logOptions);
                        this.storeSessionAndNavigate(newCompanyId, collaboratorEmail, assessmentId, companyName, deepLinkState);
                    } else {
                        this.logger.info('User cancelled company switch', {}, this.logOptions);
                        const existingAssessmentId = this.tokenStore.getActiveAssessmentId();
                        if (existingAssessmentId) {
                            this.navigateToAssessmentPage(existingAssessmentId);
                        }
                    }
                })
            )
            .subscribe();
    }

    /**
     * Writes session context to localStorage and navigates to the assessment page.
     * companyId is written first (direct overwrite, no clearAll) so that other open
     * tabs detect the company change via the storage event (oldValue !== newValue).
     */
    private storeSessionAndNavigate(companyId: string, collaboratorEmail: string, assessmentId: string, companyName?: string, deepLinkState?: CollaborationDeepLinkState): void {
        if (companyId) this.tokenStore.setCompanyId(companyId);
        if (companyName) this.tokenStore.setCompanyName(companyName);
        if (collaboratorEmail) this.tokenStore.setCollaboratorEmail(collaboratorEmail);
        this.tokenStore.clearActiveAssessmentId();
        this.tokenStore.clearCollaborationErrorState();
        if (deepLinkState) this.tokenStore.setDeepLinkState(deepLinkState);

        this.logger.info('Stored invitation params, navigating to assessment', { assessmentId }, this.logOptions);
        this.navigateToAssessmentPage(assessmentId);
    }

    private extractDeepLinkState(params: Record<string, string>): CollaborationDeepLinkState | undefined {
        if (params['openCommentsPanel'] !== '1') {
            return undefined;
        }
        return {
            openCommentsPanel: true,
            sectionId: params['sectionId'] as string | undefined,
            assessmentQuestionId: params['assessmentQuestionId'] as string | undefined,
            notesThreadId: params['notesThreadId'] as string | undefined,
            agentEmail: params['agentEmail'] as string | undefined,
        };
    }

    private async handleAuth0Callback(): Promise<void> {
        try {
            const result = await firstValueFrom(this.auth.handleRedirectCallback());
            const appState = result?.appState as { target?: string } | undefined;
            const targetUrl = appState?.target;

            if (!targetUrl) {
                this.logger.warn('Auth0 callback without appState.target', {}, this.logOptions);
                this.navigateToAuthenticationErrorPage(
                    this.contentPipe.transform('ai-assessments-collaboration.generalAuthFailureMessage', {
                        params: { error: 'No redirect target in Auth0 callback' }
                    })
                );
                return;
            }

            const targetParams = new URLSearchParams(targetUrl.split('?')[1] || '');
            const assessmentId = targetParams.get('assessmentPageId');

            if (!assessmentId) {
                this.logger.warn('Auth0 callback appState.target missing assessmentPageId', { targetUrl }, this.logOptions);
                this.navigateToAuthenticationErrorPage(
                    this.contentPipe.transform('ai-assessments-collaboration.generalAuthFailureMessage', {
                        params: { error: 'No assessmentId in Auth0 callback' }
                    })
                );
                return;
            }

            const companyId = targetParams.get('companyId');
            const collaboratorEmail = targetParams.get('email');
            const companyName = targetParams.get('companyName') ?? undefined;

            if (!companyId || !collaboratorEmail) {
                this.logger.warn('Auth0 callback appState.target missing companyId or email', {
                    targetUrl,
                    hasCompanyId: !!companyId,
                    hasEmail: !!collaboratorEmail,
                }, this.logOptions);
                this.navigateToAuthenticationErrorPage(
                    this.contentPipe.transform('ai-assessments-collaboration.generalAuthFailureMessage', {
                        params: { error: 'Incomplete authentication state in Auth0 callback' }
                    })
                );
                return;
            }

            const isValid = await this.tokenService.verifyTokenAndSession();

            if (!isValid) {
                this.logger.warn('Failed to verify Auth0 session after callback', { assessmentId }, this.logOptions);
                this.navigateToAuthenticationErrorPage(
                    this.contentPipe.transform('ai-assessments-collaboration.generalAuthFailureMessage', {
                        params: { error: 'Failed to verify session after Auth0 callback' }
                    })
                );
                return;
            }

            this.logger.info('Auth0 session verified, navigating to assessment page', { assessmentId }, this.logOptions);
            this.tokenStore.setCompanyId(companyId);
            this.tokenStore.setCollaboratorEmail(collaboratorEmail);
            if (companyName) this.tokenStore.setCompanyName(companyName);
            this.tokenStore.setActiveAssessmentId(assessmentId);
            this.tokenStore.clearCollaborationErrorState();

            const deepLinkState = this.extractDeepLinkState({
                openCommentsPanel: targetParams.get('openCommentsPanel') ?? '',
                sectionId: targetParams.get('sectionId') ?? '',
                assessmentQuestionId: targetParams.get('assessmentQuestionId') ?? '',
                notesThreadId: targetParams.get('notesThreadId') ?? '',
                agentEmail: targetParams.get('agentEmail') ?? '',
            });
            if (deepLinkState) this.tokenStore.setDeepLinkState(deepLinkState);

            this.navigateToAssessmentPage(assessmentId);
        } catch (error) {
            const errorCode = (error as any)?.error as string | undefined;
            const errorMessage = (error as Error)?.message || String(error);
            this.logger.logError(error as Error, {
                error: errorMessage,
                errorCode,
                errorStack: (error as Error)?.stack,
            }, this.logOptions);
            this.navigateToAuthenticationErrorPage(errorCode ? `${errorMessage} (${errorCode})` : errorMessage);
        }
    }

    private navigateToAssessmentPage(assessmentId: string): void {
        this.navigationService.navigateToAssessmentPage(assessmentId);
    }

    private navigateToAuthenticationErrorPage(error: Error | string): void {
        this.navigationService.navigateToAuthenticationErrorPage(error);
    }
}
